import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { useExecuteTableViewAnalysisService } from '@/services/tableViews';
import type { TableViewColumnDto, TableViewRowDto } from '@ditto/shared';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useEffect } from 'react';
import { toast } from 'sonner';

interface UseErrorRowsToastOptions {
  tableViewId: string | undefined;
  rows: TableViewRowDto[] | undefined;
  columns: TableViewColumnDto[];
  isGenerating: boolean;
}

/**
 * Hook that shows a toast notification when there are rows with generation errors.
 * Provides a regenerate button to retry failed rows.
 */
export const useErrorRowsToast = ({
  tableViewId,
  rows,
  columns,
  isGenerating,
}: UseErrorRowsToastOptions) => {
  const { executeAnalysis } = useExecuteTableViewAnalysisService();

  useEffect(() => {
    const errorRows = rows?.filter(row =>
      row.cells.some(cell => cell.generationStatus === 'error')
    );

    const errorCount = errorRows?.length ?? 0;

    const handleRegenerate = () => {
      executeAnalysis({
        tableViewId: tableViewId ?? '',
        data: {
          columnIds: columns.map(col => col.id),
          rowIds: errorRows?.map(row => row.id) ?? [],
        },
      });
    };

    if (errorCount > 0 && !isGenerating) {
      toast.custom(
        () => (
          <div className="-translate-x-32">
            <FlexRow
              gap="3"
              alignItems="center"
              className="w-[500px] bg-white border border-neutral-200 rounded-lg shadow-lg"
              justifyContent="between"
              p="4"
            >
              <AlertTriangle
                width={24}
                height={24}
                className="text-neutral-900 shrink-0"
              />
              <FlexCol gap="1" className="w-fit">
                <Text
                  variant="body"
                  className="text-neutral-900 font-weight-500"
                >
                  Some rows could not be generated.
                </Text>
                <Text
                  variant="body-sm"
                  className="text-neutral-700 font-weight-400"
                >
                  AI generation failed for {errorCount} row
                  {errorCount > 1 ? 's' : ''}. Retry by regenerating only the
                  affected rows.
                </Text>
              </FlexCol>
              <Button
                variant="dark"
                className="gap-2"
                onClick={handleRegenerate}
              >
                <RefreshCw className="size-4" />
                Regenerate
              </Button>
            </FlexRow>
          </div>
        ),
        {
          id: 'generation-errors',
          position: 'bottom-right',
          duration: 10000,
        }
      );
    } else {
      toast.dismiss('generation-errors');
    }
  }, [rows, columns, tableViewId, isGenerating, executeAnalysis]);
};
